import React, {useCallback, useEffect, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate, useParams} from 'react-router-dom';

import {ErrorBoundary} from '@sentry/react';
import {clearSelection} from 'src/components/data-table-selection';
import {selectDataTableSelection} from 'src/components/data-table-selection/services/data-table-selection-slice';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {USER_TABS} from 'src/constants';
import {useConfirmationChunk} from 'src/contexts/confirmation-context';
import {useDataTableColumnsWidthOnResize} from 'src/hooks/useDataTableColumnsWidthOnResize';
import {useEffectWithDeps} from 'src/hooks/useEffectWithDeps';
import {AssignUserEntitlementsTool} from 'src/modules/bulk-update';
import {NoResultPage, PreparedEntitlementsView, ResourceSuggest, useExpandRoles} from 'src/modules/entitlements';
import {ErrorPage} from 'src/modules/error-page/error-page.components';
import {isDeepEqual} from 'src/utils/isDeepEqual';

import {BoxLoading} from '@pb/kit-component-box-loading';
import {Group} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';
import StateButton from '@pb/kit-component-state-button';
import {StickyElement} from '@pb/kit-component-sticky-element';


import {AssignUserEntitlementsPagination} from './assign-user-entitlements-pagination';
import {ResourceNameHeader} from './components/respource-name.header';
import {
    NO_ASSIGNED_ENTITLEMENT_DESCRIPTION,
    NO_ASSIGNED_ENTITLEMENT_TITLE,
    SELECTION_KEY,
} from './constants';
import {useGetAllEntitlements} from './hooks/useGetAllEntitlements';
import {assignUserEntitlementsStateActions} from './slices/assign-user-entitlements-state.slice';
import {assignUserEntitlementsActions} from './slices/assign-user-entitlements.slice';

import {
    assignUserEntitlementsControlHeader,
    assignUserEntitlementsControlHeaderSticky,
    assignUserEntitlementsLoadingContainer,
    assignUserEntitlementsTools,
} from './assign-user-entitlements.module.styl';

import type {AppState} from 'src/store/typedef';

export const AssignUserEntitlements = () => {
    const dispatch = useDispatch();
    const queryParams = useParams<{userId: string}>();
    const userId = queryParams.userId ? +queryParams.userId : -1;
    const navigate = useNavigate();

    const {getColumnWidthByColumnId, onStopResize} = useDataTableColumnsWidthOnResize();
    const {loadingError, result, cacheKeys} = useGetAllEntitlements();
    const selection = useSelector((state: AppState) => selectDataTableSelection(state, SELECTION_KEY));

    const {
        disabled: isRolesDisabled,
        isExpanded: isRolesExpanded,
        toggleRolesExpansion,
        expandedRoleIds,
        setExpandedRoleIds,
    } = useExpandRoles({records: result?.data || []});

    const {setIsChunkChanged, proceed} = useConfirmationChunk({
        chunkName: 'assign-user-entitlements',
        onProceed: () => {
            navigate(`/profile/user/${userId}/${USER_TABS.ENTITLEMENTS.name}`);
        },
    });

    const refresh = useCallback(() => {
        dispatch(clearSelection(SELECTION_KEY));
        dispatch(assignUserEntitlementsActions.clearData());
        proceed();
    }, [dispatch, proceed]);

    useEffect(() => {
        return () => {
            dispatch(clearSelection(SELECTION_KEY));
            dispatch(assignUserEntitlementsActions.clearData());
            dispatch(assignUserEntitlementsStateActions.clearData());
        };
    }, [dispatch]);

    useEffectWithDeps((prev, current) => {
        if (
            !isDeepEqual({prev: prev.selection, current: current.selection}) ||
            !isDeepEqual({prev: prev.result, current: current.result})
        ) {
            if (
                (selection.inverted && selection.ids.length !== result?.totalCount) ||
                selection.ids.length
            ) {
                setIsChunkChanged(true);
            } else {
                setIsChunkChanged(false);
            }
        }
    }, {selection, result});

    const filterResources = useCallback((resourceIds: string[] | null, resourceQuery: string) => {
        dispatch(assignUserEntitlementsStateActions.setFilters(resourceIds ? {
            resourceIds,
            resourceQuery,
        } : {}));
    }, [dispatch]);

    const noResultMessage = useMemo(() => {
        const filteredResources = cacheKeys.filters.resourceIds as string[] | undefined;
        if (filteredResources && !filteredResources.length) {
            return (
                <NoResultPage
                    title="No resources found for the selected criteria."
                    description=""
                />
            );
        }

        if (result && !result.data.length) {
            return (
                <NoResultPage
                    title={NO_ASSIGNED_ENTITLEMENT_TITLE}
                    description={NO_ASSIGNED_ENTITLEMENT_DESCRIPTION}
                />
            );
        }

        return <div/>;
    }, [cacheKeys.filters, result]);

    return (
        <ServerResponseHandler serverError={loadingError} sectionName="Assign Account Entitlements">
            <ErrorBoundary fallback={<ErrorPage subtitle="Please try again later." title="Something went wrong."/>}>
                <StickyElement className={assignUserEntitlementsControlHeaderSticky}>
                    <div className={assignUserEntitlementsControlHeader}>
                        <Group justify="space-between" alignItems="center">
                            <ResourceSuggest onSearch={filterResources} id="resource-suggest"/>
                            <Group alignItems="center">
                                <Label>Expand Roles</Label>
                                <StateButton
                                    {...{
                                        name: 'expand-roles',
                                        disabled: isRolesDisabled,
                                        checked: isRolesExpanded,
                                        onChange: toggleRolesExpansion,
                                    }}
                                />
                                <AssignUserEntitlementsTool
                                    selectionKey={SELECTION_KEY}
                                    userId={userId}
                                    onDone={refresh}
                                />
                            </Group>
                        </Group>
                    </div>
                </StickyElement>
                <BoxLoading loading={!result}>
                    <div className={assignUserEntitlementsLoadingContainer}>
                        {!!result?.data.length && (
                            <PreparedEntitlementsView
                                dataTableId="assign-account-entitlements"
                                rows={result.data}
                                selectionKey={SELECTION_KEY}
                                expandedRoleIds={expandedRoleIds}
                                setExpandedRoleIds={setExpandedRoleIds}
                                toolRibbonClassName={assignUserEntitlementsTools}
                                customOffset={137}
                                getColumnWidthByColumnId={getColumnWidthByColumnId}
                                onStopResize={onStopResize}
                                paginationStripe={<AssignUserEntitlementsPagination totalCount={result.totalCount}/>}
                                shortHeader={<ResourceNameHeader/>}
                                resourceFilter={(cacheKeys.filters.resourceQuery as string | undefined) || ''}
                            />
                        )}
                        {noResultMessage}
                    </div>
                </BoxLoading>
            </ErrorBoundary>
        </ServerResponseHandler>
    );
};
